import type { ArenaGameplayDefinition, EntityState, WorldPoint } from '../types';
import { bridgeCorridor, footprintIntersectsRiver } from './geometry';

export interface MovementResolution {
  position: WorldPoint;
  constrained: boolean;
  rejected: boolean;
  reason: 'none' | 'outside-route-corridor' | 'missing-bridge-route';
}

const limitDistance = (from: WorldPoint, to: WorldPoint, maximum: number): WorldPoint => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length <= maximum + 1e-6 || length === 0) return to;
  return { x: from.x + dx / length * maximum, y: from.y + dy / length * maximum };
};

export function constrainGroundStep(
  entity: EntityState,
  previous: WorldPoint,
  proposed: WorldPoint,
  gameplay: ArenaGameplayDefinition,
  movementBudget: number,
): MovementResolution {
  const bounded = limitDistance(previous, proposed, Math.max(0, movementBudget));
  const distanceLimited = Math.abs(bounded.x - proposed.x) > 1e-6 || Math.abs(bounded.y - proposed.y) > 1e-6;
  if (entity.movement !== 'ground' || !footprintIntersectsRiver(gameplay, bounded, entity.radius)) {
    return { position: bounded, constrained: distanceLimited, rejected: false, reason: 'none' };
  }

  const bridge = entity.route.bridgeId === null
    ? undefined
    : gameplay.bridges.find((candidate) => candidate.id === entity.route.bridgeId);
  if (!bridge) {
    return { position: { ...previous }, constrained: true, rejected: true, reason: 'missing-bridge-route' };
  }

  const corridor = bridgeCorridor(bridge, entity.radius);
  const constrainedX = Math.max(corridor.minimumX, Math.min(corridor.maximumX, bounded.x));
  const correction = Math.abs(constrainedX - bounded.x);
  if (correction > movementBudget) {
    return { position: { ...previous }, constrained: true, rejected: true, reason: 'outside-route-corridor' };
  }
  return {
    position: { x: constrainedX, y: bounded.y },
    constrained: Math.abs(constrainedX - bounded.x) > 1e-6 || distanceLimited,
    rejected: false,
    reason: constrainedX === bounded.x ? 'none' : 'outside-route-corridor',
  };
}
